// cspell:language de, en, cs, es, fi, fr, hu, it, ja, ko, nl, pl, pt, ro, ru, zh, zhTW

//! Don't forget to check dots in the end of sentences and the correct capitalization of the first letter in each sentence.

//? German & English are reference languages!

type Translations = {
	bell: object;
	getCategoriesApiCall: Record<LanguageCode, GetCategoriesApiCallTranslations>;
	center: Record<LanguageCode, CenterTranslations>;
	settings: Record<LanguageCode, SettingsTranslations>;
};

type LanguageCode = "de" | "en" | "cs" | "es" | "fi" | "fr" | "hu" | "it" | "ja" | "ko" | "nl" | "pl" | "pt" | "ro" | "ru" | "zh" | "zhTW"; // fi, ro, ru demanded by oseon

type GetCategoriesApiCallTranslations = {
	service: string;
	serviceTooltip: string;
	shopMandatory: string;
	shopMandatoryTooltip: string;
	shopOptional: string;
	shopOptionalTooltip: string;
};

type CenterTranslations = {
	settingsTooltip: string;
	noMessages: string;
	todayLabel: string;
	yesterdayLabel: string;
	olderLabel: string;
	genericError: string;
	ttlTooltip: string;
};

type SettingsTranslations = {
	page: {
		headline: string;
		text: string;
	};

	categories: {
		headline: string;
		text: string;

		table: {
			categories: {
				header: string;
			};
			notifications: {
				header: string;
				tooltip: string;
			};
			emails: {
				header: string;
				tooltip: string;
			};
			severities: {
				header: string;
				tooltip: string;
				standard: string;
				elevated: string;
				critical: string;
			};
		};
	};

	assets: {
		headline: string;
		text: string;
		filterPlaceholder: string;
		table: {
			allAssetsTooltip: string;
			myAssets: string;
			unconfiguredAssetsTooltip: string;
		};
	};

	genericError: string;
	save: string;
	cancel: string;
};

const translations: Translations = {
	bell: {
		// no translations needed
	},

	getCategoriesApiCall: {
		de: {
			service: "Service",
			serviceTooltip:
				"Bei aktivierter Checkbox erhalten Sie Service-Benachrichtigungen und Hinweise zu notwendigen Aktionen Ihrerseits (u.a. Updates zu Servicefällen, proaktive Servicefälle, Verfügbarkeit von Technical Guides).",

			shopMandatory: "Bestellfreigabeprozess (verpflichtend)",
			shopMandatoryTooltip:
				"Sie erhalten Benachrichtigungen und Hinweise zu notwendigen Aktionen Ihrerseits (Freigabe oder Ablehnung von Bestellanfragen), sowie Statusänderung Ihrer Bestellanfrage.",

			shopOptional: "Bestellfreigabeprozess (Erinnerung)",
			shopOptionalTooltip: "Bei aktivierter Checkbox erhalten Sie eine Erinnerung bei vorliegenden offenen Bestellanfragen.",
		},
		en: {
			service: "Service",
			serviceTooltip:
				"If the checkbox is activated, you will receive service notifications and information about necessary actions on your part (e.g. updates on service cases, proactive service cases, availability of technical guides).",

			shopMandatory: "Order approval process (mandatory)",
			shopMandatoryTooltip:
				"You will receive notifications and information about necessary actions on your part (approval or rejection of order requests), as well as changes to the status of your order request.",

			shopOptional: "Order approval process (reminder)",
			shopOptionalTooltip: "If the checkbox is activated, you will receive a reminder when there are open order requests.",
		},

		cs: {
			service: "Služba",
			serviceTooltip: "Pokud je zaškrtávací políčko aktivní, obdržíte servisní oznámení a informace o nezbytných krocích z vaší strany (např. aktualizace servisních případů, proaktivní servisní případy, dostupnost technických příruček).",

			shopMandatory: "Proces schvalování objednávek (povinný)",
			shopMandatoryTooltip: "Obdržíte oznámení a informace o nezbytných krocích z vaší strany (schválení nebo zamítnutí žádostí o objednávku), stejně jako změny stavu vaší žádosti o objednávku.",

			shopOptional: "Proces schvalování objednávek (připomenutí)",
			shopOptionalTooltip: "Pokud je zaškrtávací políčko aktivní, obdržíte připomenutí, když budou existovat nevyřízené žádosti o objednávku.",
		},
		es: {
			service: "Servicio",
			serviceTooltip: "Si la casilla está activada, recibirá notificaciones de servicio e información sobre las acciones necesarias por su parte (por ejemplo, actualizaciones sobre casos de servicio, casos de servicio proactivos, disponibilidad de guías técnicas).",

			shopMandatory: "Proceso de aprobación de pedidos (obligatorio)",
			shopMandatoryTooltip: "Recibirá notificaciones e información sobre las acciones necesarias por su parte (aprobación o rechazo de solicitudes de pedido), así como cambios en el estado de su solicitud de pedido.",

			shopOptional: "Proceso de aprobación de pedidos (recordatorio)",
			shopOptionalTooltip: "Si la casilla está activada, recibirá un recordatorio cuando haya solicitudes de pedido abiertas.",
		},
		fi: {
			service: "Palvelu",
			serviceTooltip: "Jos valintaruutu on aktivoitu, saat palveluilmoituksia ja tietoa tarvittavista toimenpiteistä (esim. päivitykset palvelutapauksiin, ennakoivat palvelutapaukset, teknisten oppaiden saatavuus).",

			shopMandatory: "Tilausten hyväksymisprosessi (pakollinen)",
			shopMandatoryTooltip: "Saat ilmoituksia ja tietoa tarvittavista toimenpiteistä (tilausten hyväksyntä tai hylkäys) sekä muutoksista tilauksesi tilaan.",

			shopOptional: "Tilausten hyväksymisprosessi (muistutus)",
			shopOptionalTooltip: "Jos valintaruutu on aktivoitu, saat muistutuksen, kun avoimia tilauspyyntöjä on olemassa.",
		},
		fr: {
			service: "Service",
			serviceTooltip: "Si la case à cocher est activée, vous recevrez des notifications de service et des informations sur les actions nécessaires de votre part (par exemple, mises à jour sur les cas de service, cas de service proactifs, disponibilité des guides techniques).",

			shopMandatory: "Processus d'approbation des commandes (obligatoire)",
			shopMandatoryTooltip: "Vous recevrez des notifications et des informations sur les actions nécessaires de votre part (approbation ou rejet des demandes de commande), ainsi que des changements de statut de votre demande de commande.",

			shopOptional: "Processus d'approbation des commandes (rappel)",
			shopOptionalTooltip: "Si la case à cocher est activée, vous recevrez un rappel lorsqu'il y aura des demandes de commande en attente.",
		},
		hu: {
			service: "Szolgáltatás",
			serviceTooltip: "Ha a jelölőnégyzet be van jelölve, szolgáltatási értesítéseket és információkat kap a szükséges teendőkről (pl. szervizesetek frissítései, proaktív szervizesetek, technikai útmutatók elérhetősége).",

			shopMandatory: "Rendelésjóváhagyási folyamat (kötelező)",
			shopMandatoryTooltip: "Értesítéseket és információkat kap a szükséges teendőkről (rendelési kérelmek jóváhagyása vagy elutasítása), valamint a rendelési kérelme állapotának változásairól.",

			shopOptional: "Rendelésjóváhagyási folyamat (emlékeztető)",
			shopOptionalTooltip: "Ha a jelölőnégyzet be van jelölve, emlékeztetőt kap, ha nyitott rendelési kérelmek vannak.",
		},
		it: {
			service: "Servizio",
			serviceTooltip: "Se la casella di controllo è attivata, riceverà notifiche di servizio e informazioni sulle azioni necessarie da parte sua (ad es. aggiornamenti sui casi di assistenza, casi di assistenza proattivi, disponibilità di guide tecniche).",

			shopMandatory: "Processo di approvazione degli ordini (obbligatorio)",
			shopMandatoryTooltip: "Riceverà notifiche e informazioni sulle azioni necessarie da parte sua (approvazione o rifiuto delle richieste d'ordine), nonché sulle modifiche allo stato della sua richiesta d'ordine.",

			shopOptional: "Processo di approvazione degli ordini (promemoria)",
			shopOptionalTooltip: "Se la casella di controllo è attivata, riceverà un promemoria quando ci sono richieste d'ordine aperte.",
		},
		ja: {
			service: "サービス",
			serviceTooltip: "チェックボックスが有効な場合、サービス通知や必要な対応に関する情報（例：サービス案件の更新、プロアクティブなサービス案件、技術ガイドの利用可能性）を受け取ります。",

			shopMandatory: "注文承認プロセス（必須）",
			shopMandatoryTooltip: "必要な対応（注文依頼の承認または却下）や注文依頼のステータス変更に関する通知と情報を受け取ります。",

			shopOptional: "注文承認プロセス（リマインダー）",
			shopOptionalTooltip: "チェックボックスが有効な場合、未処理の注文依頼がある場合にリマインダーを受け取ります。",
		},
		ko: {
			service: "서비스",
			serviceTooltip: "체크박스가 활성화되면 서비스 알림 및 필요한 조치에 대한 정보를 받게 됩니다(예: 서비스 사례 업데이트, 사전 예방적 서비스 사례, 기술 가이드 이용 가능 여부 등).",

			shopMandatory: "주문 승인 프로세스(필수)",
			shopMandatoryTooltip: "필요한 조치(주문 요청 승인 또는 거부) 및 주문 요청 상태 변경에 대한 알림과 정보를 받게 됩니다.",

			shopOptional: "주문 승인 프로세스(알림)",
			shopOptionalTooltip: "체크박스가 활성화되면 미처리 주문 요청이 있을 때 알림을 받게 됩니다.",
		},
		nl: {
			service: "Service",
			serviceTooltip: "Als het selectievakje is ingeschakeld, ontvangt u serviceberichten en informatie over noodzakelijke acties van uw kant (bijv. updates over servicegevallen, proactieve servicegevallen, beschikbaarheid van technische handleidingen).",

			shopMandatory: "Goedkeuringsproces voor bestellingen (verplicht)",
			shopMandatoryTooltip:
				"U ontvangt meldingen en informatie over noodzakelijke acties van uw kant (goedkeuring of afwijzing van bestelaanvragen), evenals wijzigingen in de status van uw bestelaanvraag.",

			shopOptional: "Goedkeuringsproces voor bestellingen (herinnering)",
			shopOptionalTooltip: "Als het selectievakje is ingeschakeld, ontvangt u een herinnering wanneer er openstaande bestelaanvragen zijn.",
		},
		pl: {
			service: "Serwis",
			serviceTooltip: "Jeśli pole wyboru jest aktywne, otrzymasz powiadomienia serwisowe oraz informacje o niezbędnych działaniach z Twojej strony (np. aktualizacje spraw serwisowych, proaktywne sprawy serwisowe, dostępność przewodników technicznych).",

			shopMandatory: "Proces zatwierdzania zamówień (obowiązkowy)",
			shopMandatoryTooltip:
				"Otrzymasz powiadomienia i informacje o niezbędnych działaniach z Twojej strony (zatwierdzenie lub odrzucenie wniosków o zamówienie), a także o zmianach statusu Twojego wniosku o zamówienie.",

			shopOptional: "Proces zatwierdzania zamówień (przypomnienie)",
			shopOptionalTooltip: "Jeśli pole wyboru jest aktywne, otrzymasz przypomnienie, gdy będą otwarte wnioski o zamówienie.",
		},
		pt: {
			service: "Serviço",
			serviceTooltip: "Se a caixa de seleção estiver ativada, receberá notificações de serviço e informações sobre as ações necessárias da sua parte (por exemplo, atualizações sobre casos de serviço, casos de serviço proativos, disponibilidade de guias técnicos).",
			shopMandatory: "Processo de aprovação de pedidos (obrigatório)",
			shopMandatoryTooltip: "Receberá notificações e informações sobre as ações necessárias da sua parte (aprovação ou rejeição de pedidos), bem como alterações ao estado do seu pedido.",
			shopOptional: "Processo de aprovação de pedidos (lembrete)",
			shopOptionalTooltip: "Se a caixa de seleção estiver ativada, receberá um lembrete quando existirem pedidos em aberto.",
		},
		ro: {
			service: "Serviciu",
			serviceTooltip: "Dacă caseta de selectare este activată, veți primi notificări de serviciu și informații despre acțiunile necesare din partea dumneavoastră (de exemplu, actualizări ale cazurilor de serviciu, cazuri de serviciu proactive, disponibilitatea ghidurilor tehnice).",

			shopMandatory: "Procesul de aprobat al comenzilor (obligatoriu)",
			shopMandatoryTooltip: "Veți primi notificări și informații despre acțiunile necesare din partea dumneavoastră (aprobat sau respins cereri de comandă), precum și despre modificările stării cererii dumneavoastră de comandă.",

			shopOptional: "Procesul de aprobat al comenzilor (reminder)",
			shopOptionalTooltip: "Dacă caseta de selectare este activată, veți primi un reminder atunci când există cereri de comandă deschise.",
		},
		ru: {
			service: "Сервис",
			serviceTooltip: "Если установлен флажок, вы будете получать сервисные уведомления и информацию о необходимых действиях с вашей стороны (например, обновления по сервисным случаям, проактивные сервисные случаи, доступность технических руководств).",

			shopMandatory: "Процесс утверждения заказа (обязательно)",
			shopMandatoryTooltip: "Вы будете получать уведомления и информацию о необходимых действиях с вашей стороны (утверждение или отклонение заявок на заказ), а также об изменениях статуса вашей заявки на заказ.",

			shopOptional: "Процесс утверждения заказа (напоминание)",
			shopOptionalTooltip: "Если установлен флажок, вы будете получать напоминание при наличии открытых заявок на заказ.",
		},
		zh: {
			service: "服务",
			serviceTooltip: "如果选中复选框，您将收到服务通知以及有关您需要采取的必要操作的信息（例如，服务案例更新、主动服务案例、技术指南的可用性）。",

			shopMandatory: "订单审批流程（必填）",
			shopMandatoryTooltip: "您将收到有关您需要采取的必要操作（批准或拒绝订单请求）以及订单请求状态变更的通知和信息。",

			shopOptional: "订单审批流程（提醒）",
			shopOptionalTooltip: "如果选中复选框，当有未处理的订单请求时，您将收到提醒。",
		},
		zhTW: {
			service: "服務",
			serviceTooltip: "啟用核取方塊後，您將收到服務通知以及有關您需執行之必要操作的資訊（例如：服務案件更新、主動服務案件、技術指南的可用性）。",

			shopMandatory: "訂單審核流程（必填）",
			shopMandatoryTooltip: "您將收到有關您需執行之必要操作（批准或拒絕訂單申請）以及訂單申請狀態變更的通知與資訊。",

			shopOptional: "訂單審核流程（提醒）",
			shopOptionalTooltip: "啟用核取方塊後，當有未處理的訂單申請時，您將收到提醒。",
		},
	},

	center: {
		de: {
			settingsTooltip: "Benachrichtigungseinstellungen",
			noMessages: "Es liegen derzeit keine Nachrichten für Sie vor.",
			todayLabel: "Heute",
			yesterdayLabel: "Gestern",
			olderLabel: "Älter",
			genericError: "Ein Fehler ist aufgetreten. Versuchen Sie es später bitte erneut.",
			ttlTooltip: "Die Benachrichtigung wird nach der angezeigten Zeit automatisch gelöscht",
		},
		en: {
			settingsTooltip: "Notification Settings",
			noMessages: "There are currently no messages for you.",
			todayLabel: "Today",
			yesterdayLabel: "Yesterday",
			olderLabel: "Older",
			genericError: "An error has occurred. Please try again later.",
			ttlTooltip: "The notification is automatically deleted after the time indicated",
		},

		cs: {
			settingsTooltip: "Nastavení oznámení",
			noMessages: "Nyní pro Vás nejsou k dispozici žádná nová hlášení.",
			todayLabel: "Dnes",
			yesterdayLabel: "Včera",
			olderLabel: "Starší",
			genericError: "Došlo k chybě. Zkuste to prosím znovu později.",
			ttlTooltip: "Zpráva bude po zobrazené době automaticky vymazána.",
		},
		es: {
			settingsTooltip: "Configuración de notificaciones",
			noMessages: "Actualmente no hay mensajes nuevos para ti.",
			todayLabel: "Hoy",
			yesterdayLabel: "Ayer",
			olderLabel: "Anteriores",
			genericError: "Ha ocurrido un error. Por favor, inténtelo de nuevo más tarde.",
			ttlTooltip: "La notificación se borrará automáticamente una vez transcurrido el tiempo estipulado",
		},
		fi: {
			settingsTooltip: "Ilmoitusasetukset",
			noMessages: "Tällä hetkellä sinulle ei ole viestejä.",
			todayLabel: "Tänään",
			yesterdayLabel: "Eilen",
			olderLabel: "Aiemmat",
			genericError: "Tapahtui virhe. Yritä myöhemmin uudelleen.",
			ttlTooltip: "Ilmoitus poistetaan automaattisesti ilmoitetun ajan kuluttua.",
		},
		fr: {
			settingsTooltip: "Paramètres de notification",
			noMessages: "Vous n'avez actuellement aucun message.",
			todayLabel: "Aujourd'hui",
			yesterdayLabel: "Hier",
			olderLabel: "Date plus ancienne",
			genericError: "Une erreur s'est produite. Veuillez réessayer plus tard.",
			ttlTooltip: "La notification est supprimée automatiquement après expiration du délai indiqué.",
		},
		hu: {
			settingsTooltip: "Értesítési beállítások",
			noMessages: "Jelenleg Önnek nincsenek új üzenetei.",
			todayLabel: "Ma",
			yesterdayLabel: "Tegnap",
			olderLabel: "Régebbi",
			genericError: "Hiba történt. Kérjük, próbálja meg később újra.",
			ttlTooltip: "Az értesítés a kijelzett idő elteltével törlődik",
		},
		it: {
			settingsTooltip: "Impostazioni di notifica",
			noMessages: "Al momento non ci sono messaggi.",
			todayLabel: "Oggi",
			yesterdayLabel: "Ieri",
			olderLabel: "Precedenti",
			genericError: "Si è verificato un errore. Per favore riprova più tardi.",
			ttlTooltip: "La notifica viene eliminata automaticamente dopo il tempo indicato",
		},
		ja: {
			settingsTooltip: "通知設定",
			noMessages: "現在、お客様宛の通知はありません。",
			todayLabel: "今日",
			yesterdayLabel: "昨日",
			olderLabel: "それ以前",
			genericError: "エラーが発生しました。後でもう一度お試しください。",
			ttlTooltip: "通知は、表示時間後に自動的に削除されます。",
		},
		ko: {
			settingsTooltip: "알림 설정",
			noMessages: "현재 새 메시지가 없습니다.",
			todayLabel: "오늘",
			yesterdayLabel: "어제",
			olderLabel: "이전",
			genericError: "오류가 발생했습니다. 나중에 다시 시도해 주세요.",
			ttlTooltip: "알림은 표시된 시간이 지나면 자동으로 삭제됩니다",
		},
		nl: {
			settingsTooltip: "Meldingsinstellingen",
			noMessages: "Er zijn momenteel voor u geen meldingen.",
			todayLabel: "Vandaag",
			yesterdayLabel: "Gisteren",
			olderLabel: "Ouder",
			genericError: "Er is een fout opgetreden. Probeer het later opnieuw.",
			ttlTooltip: "Het bericht wordt na de weergegeven tijd automatisch gewist",
		},
		pl: {
			settingsTooltip: "Ustawienia powiadomień",
			noMessages: "Obecnie nie mają Państwo żadnych wiadomości.",
			todayLabel: "Dzisiaj",
			yesterdayLabel: "Wczoraj",
			olderLabel: "Starsze",
			genericError: "Wystąpił błąd. Proszę spróbować ponownie później.",
			ttlTooltip: "Powiadomienie zostanie automatycznie usunięte po upływie wskazanego czasu",
		},
		pt: {
			settingsTooltip: "Definições de notificação",
			noMessages: "De momento, não existem notificações para o(a) senhor(a).",
			todayLabel: "Hoje",
			yesterdayLabel: "Ontem",
			olderLabel: "Mais antigas",
			genericError: "Ocorreu um erro. Por favor, tente novamente mais tarde.",
			ttlTooltip: "A notificação será eliminada automaticamente após o tempo indicado.",
		},
		ro: {
			settingsTooltip: "Setări notificări",
			noMessages: "Momentan nu aveți mesaje.",
			todayLabel: "Astăzi",
			yesterdayLabel: "Ieri",
			olderLabel: "Mai vechi",
			genericError: "A apărut o eroare. Vă rugăm să încercați din nou mai târziu.",
			ttlTooltip: "Notificarea va fi ștearsă automat după timpul indicat.",
		},
		ru: {
			settingsTooltip: "Настройки уведомлений",
			noMessages: "В настоящее время у вас нет сообщений.",
			todayLabel: "Сегодня",
			yesterdayLabel: "Вчера",
			olderLabel: "Более ранние",
			genericError: "Произошла ошибка. Пожалуйста, попробуйте еще раз позже.",
			ttlTooltip: "Уведомление будет автоматически удалено по истечении указанного времени.",
		},
		zh: {
			settingsTooltip: "通知设置",
			noMessages: "目前没有给您的新信息提示。",
			todayLabel: "今天",
			yesterdayLabel: "昨天",
			olderLabel: "更早",
			genericError: "发生了一个错误。请稍后再试。",
			ttlTooltip: "显示的时间过后，通知会自动删除",
		},
		zhTW: {
			settingsTooltip: "通知設定",
			noMessages: "目前沒有給您的訊息。",
			todayLabel: "現在",
			yesterdayLabel: "昨天",
			olderLabel: "更早",
			genericError: "發生了錯誤。請稍後再試。",
			ttlTooltip: "訊息會在顯示時間後自動刪除",
		},
	},

	settings: {
		de: {
			page: {
				headline: "Benachrichtigungseinstellungen",
				text: "Konfigurieren Sie Ihre Benachrichtigungen.",
			},

			categories: {
				headline: "Kategorien und Kanäle",
				text: "Wählen Sie die Kategorien und Kanäle für den Empfang von Benachrichtigungen aus.",
				table: {
					categories: {
						header: "Kategorie",
					},
					notifications: {
						header: "Benachrichtigung",
						tooltip: "Bei aktivierter Checkbox empfangen Sie Benachrichtigungen in MyTRUMPF.",
					},
					emails: {
						header: "E-Mail",
						tooltip:
							"Bei aktivierter Checkbox empfangen Sie Benachrichtigungen per E-Mail. Bitte beachten Sie, dass die E-Mail-Benachrichtigungen hier Ihre individuellen und vorgangsbezogenen Benachrichtigungen beinhalten. Sie können sich an dieser Stelle nicht von TRUMPF Newslettern abmelden.",
					},
					severities: {
						header: "Benachrichtigungslevel", //? Benachrichtigungsstufe
						tooltip: "Setzen Sie das Benachrichtigungslevel für sämtliche Kanäle einer Kategorie.", //?  Legen Sie die Benachrichtigungsstufe für alle Kanäle innerhalb einer Kategorie fest.
						standard: "Standard (Information, Warnung, Fehler)",
						elevated: "Dringlich (Warnung, Fehler)",
						critical: "Kritisch (Fehler)",
					},
				},
			},

			assets: {
				headline: "Asset Filter",
				text: "Filtern Sie Benachrichtigungen aus den oben gewählten Kategorien zusätzlich nach Assets.",
				filterPlaceholder: "Assets nach Name oder ID filtern",
				table: {
					allAssetsTooltip: "Alle sichtbaren Assets",
					myAssets: "Meine Maschinen", //! my assets would be more appropriate
					unconfiguredAssetsTooltip:
						"Sie haben nicht konfigurierte Assets*. Standardmäßig erhalten Sie Benachrichtigungen von diesen Assets, es sei denn, Sie deaktivieren sie.",
				},
			},

			genericError: "Ein Fehler ist aufgetreten. Versuchen Sie es später bitte erneut.",
			save: "Speichern",
			cancel: "Abbrechen",
		},

		en: {
			page: {
				headline: "Notification settings",
				text: "Configure your notifications.",
			},

			categories: {
				headline: "Categories and channels",
				text: "Select the categories and channels for receiving notifications.",
				table: {
					categories: {
						header: "Category",
					},
					notifications: {
						header: "Notification",
						tooltip: "With the checkbox activated, you will receive notifications in MyTRUMPF.",
					},
					emails: {
						header: "Email",
						tooltip:
							"With the checkbox activated, you will receive notifications via e-mail. Please note that e-mail notifications here include your individual and transactional notifications. You can unsubscribe from TRUMPF newsletters here.",
					},
					severities: {
						header: "Notification Level",
						tooltip: "Set the notification level for all channels of a category.",
						standard: "Standard (Information, Warning, Error)",
						elevated: "Elevated (Warning, Error)",
						critical: "Critical (Error)",
					},
				},
			},

			assets: {
				headline: "Asset Filter",
				text: "Also filter the notifications from the categories selected above according to assets.", //! this one can't be correct - it'S terrible
				filterPlaceholder: "Filter assets by name or ID",
				table: {
					allAssetsTooltip: "All visible assets",
					myAssets: "My Machines", //! my assets would be more appropriate
					unconfiguredAssetsTooltip: "You have unconfigured assets*. By default, you will receive notifications from these assets unless you disable them.",
				},
			},

			genericError: "An error has occurred. Please try again later.",
			save: "Save",
			cancel: "Cancel",
		},

		cs: {
			page: {
				headline: "Nastavení zpráv",
				text: "Nakonfigurujte Vaše zprávy.",
			},

			categories: {
				headline: "Kategorie a kanály",
				text: "Zvolte kategorie a kanály pro příjem zpráv.",
				table: {
					categories: {
						header: "Kategorie",
					},
					notifications: {
						header: "Zpráva",
						tooltip: "Když bude aktivované zaškrtávací políčko, budete přijímat zprávy v MyTRUMPF.",
					},
					emails: {
						header: "E-Mail",
						tooltip:
							"Když bude aktivované zaškrtávací políčko, budete přijímat zprávy v e-mailu. Vezměte prosím v úvahu, že zprávy zasílané e-mailem obsahují Vaše individuální a upřednostněné zprávy. Na tomto místě nemůžete odhlásit přijímání Newsletterů.",
					},
					severities: {
						header: "Úroveň oznámení",
						tooltip: "Nastavte úroveň oznámení pro všechny kanály v rámci kategorie.",
						standard: "Standardní (Informace, Varování, Chyba)",
						elevated: "Zvýšená (Varování, Chyba)",
						critical: "Kritická (Chyba)",
					},
				},
			},

			assets: {
				headline: "Asset filtr",
				text: "Filtrujte zprávy z výše zvolených kategorií na doplnění podle Assets.",
				filterPlaceholder: "Filtrovat aktiva podle názvu nebo ID",
				table: {
					allAssetsTooltip: "Všechny viditelné položky",
					myAssets: "Moje stroje", //! whatever the existing translation is, something like My Assets would be more appropriate
					unconfiguredAssetsTooltip: "Máte nekonfigurovaná aktiva*. Ve výchozím nastavení budete dostávat oznámení o těchto aktivech, pokud je nevypnete.",
				},
			},

			genericError: "Došlo k chybě. Zkuste to prosím znovu později.",
			save: "Uložit",
			cancel: "Zrušit",
		},

		es: {
			page: {
				headline: "Configuración de notificaciones",
				text: "Configura tus notificaciones.",
			},

			categories: {
				headline: "Categorías y canales",
				text: "Selecciona las categorías y los canales para recibir notificaciones.",
				table: {
					categories: {
						header: "Categoría",
					},
					notifications: {
						header: "Notificación",
						tooltip: "Si activas la casilla de verificación, recibirás notificaciones en MyTRUMPF.",
					},
					emails: {
						header: "Correo electrónico",
						tooltip:
							"Si activas la casilla de verificación, recibirás notificaciones por correo electrónico. Ten en cuenta que las notificaciones por correo electrónico incluyen tus notificaciones individuales y las relacionadas con los procesos. No puedes darte de baja de los boletines informativos de TRUMPF.",
					},
					severities: {
						header: "Nivel de notificación",
						tooltip: "Establezca el nivel de notificación para todos los canales de una categoría.",
						standard: "Estándar (Información, Advertencia, Error)",
						elevated: "Elevado (Advertencia, Error)",
						critical: "Crítico (Error)",
					},
				},
			},

			assets: {
				headline: "Filtro de activos",
				text: "Filtra las notificaciones de las categorías seleccionadas anteriormente por activos.",
				filterPlaceholder: "Filtrar activos por nombre o ID",
				table: {
					allAssetsTooltip: "Todos los activos visibles",
					myAssets: "Mis máquinas", //! whatever the existing translation is, something like My Assets would be more appropriate
					unconfiguredAssetsTooltip:
						"Tiene activos* no configurados. De forma predeterminada, recibirá notificaciones de estos activos a menos que los desactive.",
				},
			},

			genericError: "Ha ocurrido un error. Por favor, inténtelo de nuevo más tarde.",
			save: "Guardar",
			cancel: "Cancelar",
		},

		fi: {
			page: {
				headline: "Ilmoitusasetukset",
				text: "Määritä ilmoituksesi.",
			},

			categories: {
				headline: "Kategoriat ja kanavat",
				text: "Valitse kategoriat ja kanavat ilmoitusten vastaanottamista varten.",
				table: {
					categories: {
						header: "Kategoria",
					},
					notifications: {
						header: "Ilmoitus",
						tooltip: "Kun valintaruutu on aktivoitu, saat ilmoituksia MyTRUMPFissa.",
					},
					emails: {
						header: "Sähköposti",
						tooltip: "Kun valintaruutu on aktivoitu, saat ilmoituksia sähköpostitse. Huomaa, että sähköposti-ilmoitukset sisältävät henkilökohtaiset ja prosessikohtaiset ilmoituksesi. Tässä et voi peruuttaa TRUMPF-uutiskirjeiden tilausta.",
					},
					severities: {
						header: "Ilmoitustaso",
						tooltip: "Aseta ilmoitustaso kaikille kategorian kanaville.",
						standard: "Vakio (Informaatio, Varoitus, Virhe)",
						elevated: "Korotettu (Varoitus, Virhe)",
						critical: "Kriittinen (Virhe)",
					},
				},
			},

			assets: {
				headline: "Omaisuusfiltteri",
				text: "Suodata yllä valittujen kategorioiden ilmoituksia myös omaisuuksien mukaan.",
				filterPlaceholder: "Suodata omaisuuksia nimen tai tunnisteen mukaan",
				table: {
					allAssetsTooltip: "Kaikki näkyvät omaisuudet",
					myAssets: "Omat koneet",
					unconfiguredAssetsTooltip: "Sinulla on määrittämättömiä omaisuuksia*. Oletuksena saat ilmoituksia näistä omaisuuksista, ellet poista niitä käytöstä.",
				},
			},

			genericError: "Tapahtui virhe. Yritä myöhemmin uudelleen.",
			save: "Tallenna",
			cancel: "Peruuta",
		},

		fr: {
			page: {
				headline: "Réglages de notification",
				text: "Configurez vos notifications.",
			},

			categories: {
				headline: "Catégories et canaux",
				text: "Sélectionnez les catégories et les canaux pour la réception des notifications.",
				table: {
					categories: {
						header: "Catégorie",
					},
					notifications: {
						header: "Notification",
						tooltip: "Si la case à cocher est activée, vous recevez des notifications dans MyTRUMPF.",
					},
					emails: {
						header: "E-mail",
						tooltip:
							"Si la case à cocher est activée, vous recevez des notifications par e-mail. Veuillez noter que les notifications par e-mail comprennent ici vos notifications personnelles et celles relatives à vos processus. Vous ne pouvez pas vous désabonner sur ce point des newsletters TRUMPF.",
					},
					severities: {
						header: "Niveau de notification",
						tooltip: "Définissez le niveau de notification pour tous les canaux d'une catégorie.",
						standard: "Standard (Information, Avertissement, Erreur)",
						elevated: "Élevé (Avertissement, Erreur)",
						critical: "Critique (Erreur)",
					},
				},
			},

			assets: {
				headline: "Filtre d'actif",
				text: "Filtrez les notifications des catégories sélectionnées ci-dessus également par actifs.",
				filterPlaceholder: "Filtrer les actifs par nom ou ID",
				table: {
					allAssetsTooltip: "Tous les actifs visibles",
					myAssets: "Mes machines", //! whatever the existing translation is, something like My Assets would be more appropriate
					unconfiguredAssetsTooltip:
						"Vous avez des actifs* non configurés. Par défaut, vous recevrez des notifications de ces actifs, sauf si vous les désactivez.",
				},
			},

			genericError: "Une erreur s'est produite. Veuillez réessayer plus tard.",
			save: "Enregistrer",
			cancel: "Annuler",
		},

		hu: {
			page: {
				headline: "Értesítések beállításai",
				text: "Konfigurálja az értesítéseit.",
			},

			categories: {
				headline: "Kategóriák és csatornák",
				text: "Válassza ki a kategóriákat és a csatornákat az értesítések fogadásához.",
				table: {
					categories: {
						header: "Kategória",
					},
					notifications: {
						header: "Értesítés",
						tooltip: "Aktivált jelölőnégyzet esetén Ön az értesítéseket a MyTRUMPF-ban kapja meg.",
					},
					emails: {
						header: "E-Mail",
						tooltip:
							"Aktivált jelölőnégyzet esetén Ön az értesítéseket e-mailen keresztül kapja meg. Kérjük, vegye figyelembe, hogy az e-mail értesítések itt az Ön egyéni és folyamatra vonatkozó értesítéseit tartalmazzák. Itt nem iratkozhat le a TRUMPF hírlevelekről.",
					},
					severities: {
						header: "Értesítési szint",
						tooltip: "Állítsa be az értesítési szintet a kategória összes csatornájára.",
						standard: "Alapértelmezett (Információ, Figyelmeztetés, Hiba)",
						elevated: "Emelt (Figyelmeztetés, Hiba)",
						critical: "Kritikus (Hiba)",
					},
				},
			},

			assets: {
				headline: "Eszköz szűrő",
				text: "Szűrje a fent kiválasztott kategóriákból érkező értesítéseket eszközök szerint is.",
				filterPlaceholder: "Eszközök szűrése név vagy azonosító alapján",
				table: {
					allAssetsTooltip: "Minden látható eszköz",
					myAssets: "A gépeim", //! whatever the existing translation is, something like My Assets would be more appropriate
					unconfiguredAssetsTooltip:
						"Nem konfigurált eszközei* vannak. Alapértelmezés szerint értesítéseket kap ezekről az eszközökről, hacsak nem tiltja le őket.",
				},
			},

			genericError: "Hiba történt. Kérjük, próbálja meg később újra.",
			save: "Mentés",
			cancel: "Mégse",
		},

		it: {
			page: {
				headline: "Impostazioni notifiche",
				text: "Configurare le proprie notifiche.",
			},

			categories: {
				headline: "Categorie e canali",
				text: "Selezionare le categorie e i canali per la ricezione delle notifiche.",
				table: {
					categories: {
						header: "Categoria",
					},
					notifications: {
						header: "Messaggio",
						tooltip: "Attivando la casella di controllo, riceverai le notifiche su MyTRUMPF.",
					},
					emails: {
						header: "E-mail",
						tooltip:
							"Attivando la casella di controllo, riceverai le notifiche via e-mail. Ti preghiamo di notare che le notifiche via e-mail qui contengono informazioni individuali e relative ai tuoi processi. A questo punto non puoi disiscriverti dalle newsletter di TRUMPF.",
					},
					severities: {
						header: "Livello di notifica",
						tooltip: "Imposta il livello di notifica per tutti i canali di una categoria.",
						standard: "Standard (Informazione, Avviso, Errore)",
						elevated: "Elevato (Avviso, Errore)",
						critical: "Critico (Errore)",
					},
				},
			},

			assets: {
				headline: "Filtro asset",
				text: "Filtrate ulteriormente per asset le notifiche dalle categorie scelte sopra.",
				filterPlaceholder: "Filtra gli asset per nome o ID",
				table: {
					allAssetsTooltip: "Tutte le asset visibili",
					myAssets: "Le mie macchine", //! whatever the existing translation is, something like My Assets would be more appropriate
					unconfiguredAssetsTooltip:
						"Hai asset* non configurati. Per impostazione predefinita, riceverai notifiche da questi asset a meno che tu li disattivi.",
				},
			},

			genericError: "Si è verificato un errore. Per favore riprova più tardi.",
			save: "Salva",
			cancel: "Annulla",
		},

		ja: {
			page: {
				headline: "通知設定",
				text: "通知を設定します。",
			},

			categories: {
				headline: "カテゴリーと経路",
				text: "通知の受信に関してカテゴリーと経路を選択します。",
				table: {
					categories: {
						header: "カテゴリー",
					},
					notifications: {
						header: "通知",
						tooltip: "このチェックボックスを有効にすると、MyTRUMPFで通知が届きます。",
					},
					emails: {
						header: "Eメール",
						tooltip:
							"このチェックボックスを有効にすると、Eメールで通知が届きます。このEメール通知には、ユーザー固有の通知とプロセスに関連する通知が含まれることにご注意ください。ここでTRUMPFニュースレターの配信を解除することはできません。",
					},
					severities: {
						header: "通知レベル",
						tooltip: "カテゴリ内のすべてのチャネルの通知レベルを設定します。",
						standard: "標準（情報、警告、エラー）",
						elevated: "高（警告、エラー）",
						critical: "重大（エラー）",
					},
				},
			},

			assets: {
				headline: "アセットフィルター",
				text: "上で選択したカテゴリーの通知を、更にアセットに基づいてフィルターします。",
				filterPlaceholder: "名前またはIDで資産をフィルタリングする",
				table: {
					allAssetsTooltip: "すべての表示中のアセット",
					myAssets: "マイマシン", //! whatever the existing translation is, something like My Assets would be more appropriate
					unconfiguredAssetsTooltip:
						"設定されていないアセット*があります。デフォルトでは、これらのアセットから通知を受け取りますが、無効にしない限り維持されます。",
				},
			},

			genericError: "エラーが発生しました。後でもう一度お試しください。",
			save: "保存",
			cancel: "キャンセル",
		},

		ko: {
			page: {
				headline: "알림 설정",
				text: "알림을 구성하십시오.",
			},

			categories: {
				headline: "카테고리 및 채널",
				text: "알림을 받을 카테고리와 채널을 선택하십시오.",
				table: {
					categories: {
						header: "카테고리",
					},
					notifications: {
						header: "전송문",
						tooltip: "확인란을 활성화하시면 MyTRUMPF에서 알림을 받으실 수 있습니다.",
					},
					emails: {
						header: "이메일",
						tooltip:
							"확인란을 활성화하시면 이메일을 통해 알림을 받으실 수 있습니다. 이메일 알림에는 개인 및 프로세스와 관련된 알림이 포함되어 있으니 이 점을 유념하시기 바랍니다. 여기서는 TRUMPF 뉴스레터 구독을 취소할 수 없습니다.",
					},
					severities: {
						header: "알림 수준",
						tooltip: "카테고리의 모든 채널에 대한 알림 수준을 설정하십시오.",
						standard: "표준(정보, 경고, 오류)",
						elevated: "상향(경고, 오류)",
						critical: "치명적(오류)",
					},
				},
			},

			assets: {
				headline: "에셋 필터",
				text: "위에서 선택한 카테고리의 알림을 추가로 에셋별로 필터링하십시오.",
				filterPlaceholder: "이름 또는 ID로 자산 필터링",
				table: {
					allAssetsTooltip: "모든 보이는 자산",
					myAssets: "나의 기계", //! whatever the existing translation is, something like My Assets would be more appropriate
					unconfiguredAssetsTooltip: "구성되지 않은 자산*이 있습니다. 기본적으로 해당 자산의 알림을 받으며, 비활성화하지 않는 한 계속 유지됩니다.",
				},
			},

			genericError: "오류가 발생했습니다. 나중에 다시 시도해 주세요.",
			save: "저장",
			cancel: "취소",
		},

		nl: {
			page: {
				headline: "Instellingen voor berichten",
				text: "Configureer uw berichten.",
			},

			categories: {
				headline: "Categorieën en kanalen",
				text: "Kies de categorieën en kanalen om berichten te ontvangen.",
				table: {
					categories: {
						header: "Categorie",
					},
					notifications: {
						header: "Bericht",
						tooltip: "Bij een geactiveerd selectievakje ontvangt u berichten in MyTRUMPF.",
					},
					emails: {
						header: "E-mail",
						tooltip:
							"Bij een geactiveerd selectievakje ontvangt u berichten per e-mail. Houd er rekening mee dat de e-mailberichten hier uw individuele en zaakgerelateerde berichten bevatten. U kunt zich op deze plek niet aanmelden voor de TRUMPF-nieuwsbrief.",
					},
					severities: {
						header: "Meldingsniveau",
						tooltip: "Stel het meldingsniveau in voor alle kanalen van een categorie.",
						standard: "Standaard (Informatie, Waarschuwing, Fout)",
						elevated: "Verhoogd (Waarschuwing, Fout)",
						critical: "Kritiek (Fout)",
					},
				},
			},

			assets: {
				headline: "Assetfilter",
				text: "Filter de berichten uit de hierboven geselecteerde categorieën bijkomend volgens asset.",
				filterPlaceholder: "Assets filteren op naam of ID",
				table: {
					allAssetsTooltip: "Alle zichtbare items",
					myAssets: "Mijn machines", //! whatever the existing translation is, something like My Assets would be more appropriate
					unconfiguredAssetsTooltip: "U heeft niet-geconfigureerde assets*. Standaard ontvangt u meldingen van deze assets, tenzij u ze uitschakelt.",
				},
			},

			genericError: "Er is een fout opgetreden. Probeer het later opnieuw.",
			save: "Opslaan",
			cancel: "Annuleren",
		},

		pl: {
			page: {
				headline: "Ustawienia powiadomień",
				text: "Skonfiguruj powiadomienia.",
			},

			categories: {
				headline: "Kategorie i kanały",
				text: "Wybierz kategorie i kanały otrzymywania powiadomień.",
				table: {
					categories: {
						header: "Kategoria",
					},
					notifications: {
						header: "Powiadomienie",
						tooltip: "Jeśli pole wyboru jest aktywne, będziesz otrzymywać powiadomienia w MyTRUMPF.",
					},
					emails: {
						header: "E-mail",
						tooltip:
							"Jeśli pole wyboru jest aktywne, będziesz otrzymywać powiadomienia pocztą elektroniczną. Należy pamiętać, że zawarte tutaj powiadomienia mailowe obejmują powiadomienia indywidualne i związane z transakcjami. W tym momencie nie można zrezygnować z newsletterów firmy TRUMPF.",
					},
					severities: {
						header: "Poziom powiadomień",
						tooltip: "Ustaw poziom powiadomień dla wszystkich kanałów danej kategorii.",
						standard: "Standardowy (Informacja, Ostrzeżenie, Błąd)",
						elevated: "Podwyższony (Ostrzeżenie, Błąd)",
						critical: "Krytyczny (Błąd)",
					},
				},
			},

			assets: {
				headline: "Filtr zasobów",
				text: "Filtruj powiadomienia z wybranych wyżej kategorii dodatkowo według zasobów.",
				filterPlaceholder: "Filtruj zasoby według nazwy lub ID",
				table: {
					allAssetsTooltip: "Wszystkie widoczne zasoby",
					myAssets: "Posiadane maszyny", //! whatever the existing translation is, something like My Assets would be more appropriate
					unconfiguredAssetsTooltip: "Masz nie skonfigurowane zasoby*. Domyślnie otrzymasz powiadomienia z tych zasobów, chyba że je wyłączysz.",
				},
			},

			genericError: "Wystąpił błąd. Proszę spróbować ponownie później.",
			save: "Zapisz",
			cancel: "Anuluj",
		},

		pt: {
			page: {
				headline: "Definições de notificação",
				text: "Configure as suas notificações.",
			},
			categories: {
				headline: "Categorias e canais",
				text: "Selecione as categorias e os canais para receber notificações.",
				table: {
					categories: {
						header: "Categoria",
					},
					notifications: {
						header: "Notificação",
						tooltip: "Com a caixa de seleção ativada, irá receber notificações na sua área MyTRUMPF.",
					},
					emails: {
						header: "E-mail",
						tooltip: "Com a caixa de seleção ativada, irá receber notificações por e-mail. Note que as notificações por e-mail aqui incluem notificações individuais e transacionais. Não é possível cancelar a subscrição das newsletters TRUMPF aqui.",
					},
					severities: {
						header: "Nível de notificação",
						tooltip: "Defina o nível de notificação para todos os canais de uma categoria.",
						standard: "Padrão (Informação, Aviso, Erro)",
						elevated: "Elevado (Aviso, Erro)",
						critical: "Crítico (Erro)",
					},
				},
			},
			assets: {
				headline: "Filtro de ativos",
				text: "Filtre também as notificações das categorias selecionadas acima de acordo com os ativos.",
				filterPlaceholder: "Filtrar ativos por nome ou ID",
				table: {
					allAssetsTooltip: "Todos os ativos visíveis",
					myAssets: "Os seus ativos",
					unconfiguredAssetsTooltip: "Existem ativos não configurados*. Por predefinição, irá receber notificações destes ativos, a menos que os desative.",
				},
			},
			genericError: "Ocorreu um erro. Por favor, tente novamente mais tarde.",
			save: "Guardar",
			cancel: "Cancelar",
		},

		ro: {
			page: {
				headline: "Setări notificări",
				text: "Configurați notificările dumneavoastră.",
			},

			categories: {
				headline: "Categorii și canale",
				text: "Selectați categoriile și canalele pentru a primi notificări.",
				table: {
					categories: {
						header: "Categorie",
					},
					notifications: {
						header: "Notificare",
						tooltip: "Dacă bifați această opțiune, veți primi notificări în MyTRUMPF.",
					},
					emails: {
						header: "E-mail",
						tooltip: "Dacă bifați această opțiune, veți primi notificări prin e-mail. Vă rugăm să rețineți că notificările prin e-mail includ notificările individuale și cele legate de procese. Nu vă puteți dezabona de la newsletterele TRUMPF aici.",
					},
					severities: {
						header: "Nivel de notificare",
						tooltip: "Setați nivelul de notificare pentru toate canalele unei categorii.",
						standard: "Standard (Informație, Avertisment, Eroare)",
						elevated: "Ridicat (Avertisment, Eroare)",
						critical: "Critic (Eroare)",
					},
				},
			},

			assets: {
				headline: "Filtru de active",
				text: "Filtrați notificările din categoriile selectate mai sus și în funcție de active.",
				filterPlaceholder: "Filtrați activele după nume sau ID",
				table: {
					allAssetsTooltip: "Toate activele vizibile",
					myAssets: "Activele mele", //! whatever the existing translation is, something like My Assets would be more appropriate
					unconfiguredAssetsTooltip: "Aveți active* neconfigurate. În mod implicit, veți primi notificări de la aceste active, cu excepția cazului în care le dezactivați.",
				},
			},

			genericError: "A apărut o eroare. Vă rugăm să încercați din nou mai târziu.",
			save: "Salvează",
			cancel: "Anulează",
		},

		ru: {
			page: {
				headline: "Настройки уведомлений",
				text: "Настройте ваши уведомления.",
			},

			categories: {
				headline: "Категории и каналы",
				text: "Выберите категории и каналы для получения уведомлений.",
				table: {
					categories: {
						header: "Категория",
					},
					notifications: {
						header: "Уведомление",
						tooltip: "Если установлен флажок, вы будете получать уведомления в MyTRUMPF.",
					},
					emails: {
						header: "Электронная почта",
						tooltip: "Если установлен флажок, вы будете получать уведомления по электронной почте. Обратите внимание, что уведомления по электронной почте включают индивидуальные и процессные уведомления. Здесь вы не можете отписаться от рассылки TRUMPF.",
					},
					severities: {
						header: "Уровень уведомления",
						tooltip: "Установите уровень уведомления для всех каналов категории.",
						standard: "Стандартный (Информация, Предупреждение, Ошибка)",
						elevated: "Повышенный (Предупреждение, Ошибка)",
						critical: "Критический (Ошибка)",
					},
				},
			},

			assets: {
				headline: "Фильтр активов",
				text: "Также фильтруйте уведомления из вышеуказанных категорий в зависимости от активов.",
				filterPlaceholder: "Фильтр активов по имени или ID",
				table: {
					allAssetsTooltip: "Все видимые активы",
					myAssets: "Мои машины", //! my assets would be more appropriate
					unconfiguredAssetsTooltip: "У вас есть неконфигурированные активы*. По умолчанию вы будете получать уведомления от этих активов, если не отключите их.",
				},
			},

			genericError: "Произошла ошибка. Пожалуйста, попробуйте еще раз позже.",
			save: "Сохранить",
			cancel: "Отмена",
		},

		zh: {
			page: {
				headline: "通知设置",
				text: "配置您的通知消息。",
			},

			categories: {
				headline: "类别和渠道",
				text: "请选择用于接收通知消息的类别和渠道。",
				table: {
					categories: {
						header: "类别",
					},
					notifications: {
						header: "通知",
						tooltip: "如果复选框被激活，您就会在 MyTRUMPF 中收到通知。",
					},
					emails: {
						header: "电子邮件",
						tooltip:
							"如果复选框被激活，您就会通过电子邮件接收通知。请注意，此处的电子邮件通知包含有您的个性化通知和过程相关的通知。在此处无法退订通快的时事通讯。",
					},
					severities: {
						header: "通知等級",
						tooltip: "為類別的所有通道設定通知等級。",
						standard: "標準（資訊、警告、錯誤）",
						elevated: "提高（警告、錯誤）",
						critical: "嚴重（錯誤）",
					},
				},
			},

			assets: {
				headline: "资产筛选器",
				text: "您可根据资产进一步筛选以上所选类别的通知消息。",
				filterPlaceholder: "按名称或 ID 筛选资产",
				table: {
					allAssetsTooltip: "所有可见的资产",
					myAssets: "我的机床", //! whatever the existing translation is, something like My Assets would be more appropriate
					unconfiguredAssetsTooltip: "您有未配置的资产*。默认情况下，您将收到这些资产的通知，除非您将其禁用。",
				},
			},

			genericError: "发生了一个错误。请稍后再试。",
			save: "保存",
			cancel: "取消",
		},

		zhTW: {
			page: {
				headline: "通知設定",
				text: "請設定您的通知。",
			},

			categories: {
				headline: "類別與通道",
				text: "請選擇接收通知的類別與通道。",
				table: {
					categories: {
						header: "類別",
					},
					notifications: {
						header: "通知",
						tooltip: "啟用核取方塊後，您將在 MyTRUMPF 收到通知。",
					},
					emails: {
						header: "電子郵件",
						tooltip: "啟用核取方塊後，您將通過電子郵件收到通知。請注意，此處的電子郵件通知包括您的個人和交易通知。您無法在此處取消訂閱 TRUMPF 電子報。",
					},
					severities: {
						header: "通知等級",
						tooltip: "為類別的所有通道設定通知等級。",
						standard: "標準（資訊、警告、錯誤）",
						elevated: "提高（警告、錯誤）",
						critical: "嚴重（錯誤）",
					},
				},
			},

			assets: {
				headline: "資產篩选器",
				text: "您可根據資產進一步篩選以上所選類別的通知消息。",
				filterPlaceholder: "按名稱或 ID 篩選資產",
				table: {
					allAssetsTooltip: "所有可見的資產",
					myAssets: "我的機器", //! whatever the existing translation is, something like My Assets would be more appropriate
					unconfiguredAssetsTooltip: "您有未配置的資產*。預設情況下，您會收到這些資產的通知，除非您將其停用。",
				},
			},

			genericError: "發生了錯誤。請稍後再試。",
			save: "儲存",
			cancel: "取消",
		},
	},
};

export const { center: centerTranslations, settings: settingsTranslations } = translations;
